'use client';

import { Button } from '@ahafood/ui/components/button';
import { Skeleton } from '@ahafood/ui/components/skeleton';
import { useRouter } from 'next/navigation';
import { useCallback, useState } from 'react';
import { FilterDrawer } from '../filter-drawer';
import { useGetFoodOrderCategories } from '@/api/use-get-category';
import { trackEvent } from '@/lib/event-tracking';

interface StoreQuickFilterProps {
  citySlug: string;
}

const buildFilteredUrl = (
  citySlug: string,
  filters: {
    category_slug?: string;
    promotion?: boolean;
    freeship?: boolean | string;
    special_request?: string;
    radius?: number;
    open_now?: boolean;
    item_tag?: string;
  }
) => {
  let url = `/${citySlug}/danh-sach-cua-hang`;
  if (filters.category_slug) url += `-${filters.category_slug}`;
  const options: string[] = [];
  if (filters.item_tag === 'SOLO_MEAL') options.push('solomeal');
  if (filters.promotion) options.push('co-khuyen-mai');
  if (filters.freeship === 'freeship' || filters.freeship === true)
    options.push('co-freeship');
  if (filters.freeship === 'freeship-100') options.push('co-freeship-100');
  if (filters.radius === 5000) options.push('gan-toi');
  if (filters.open_now) options.push('dang-mo-cua');
  if (filters.special_request === 'new_store') options.push('moi');
  if (options.length > 0) url += '-' + options.join('-');
  return url;
};

export default function StoreQuickFilter({ citySlug }: StoreQuickFilterProps) {
  const router = useRouter();

  const { data: categoriesData, isLoading } = useGetFoodOrderCategories({
    skip: 0,
    limit: 24,
  });

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [tempFilters, setTempFilters] = useState<{
    category_slug: string;
    promotion: boolean;
    freeship: boolean | string;
    special_request: string;
    nearby: boolean;
    open_now: boolean;
    item_tag?: string;
  }>({
    category_slug: '',
    promotion: false,
    freeship: false,
    special_request: '',
    nearby: false,
    open_now: false,
    item_tag: '',
  });

  const navigateWithFilters = useCallback(
    (filters: typeof tempFilters) => {
      const url = buildFilteredUrl(citySlug, {
        category_slug: filters.category_slug || undefined,
        promotion: filters.promotion,
        freeship: filters.freeship,
        special_request: filters.special_request || undefined,
        radius: filters.nearby ? 5000 : undefined,
        open_now: filters.open_now,
        item_tag: filters.item_tag ?? undefined,
      });
      router.push(url);
    },
    [citySlug, router]
  );

  const handleApplyFilters = useCallback(() => {
    navigateWithFilters(tempFilters);
    setIsFilterOpen(false);
  }, [tempFilters, navigateWithFilters]);

  const handleResetFilters = useCallback(() => {
    setTempFilters({
      category_slug: '',
      promotion: false,
      freeship: false,
      special_request: '',
      nearby: false,
      open_now: false,
    });
  }, []);

  const handleClearAllFilters = useCallback(() => {
    setTempFilters({
      category_slug: '',
      promotion: false,
      freeship: false,
      special_request: '',
      nearby: false,
      open_now: false,
    });
    router.push(`/${citySlug}/danh-sach-cua-hang`);
    setIsFilterOpen(false);
  }, [router, citySlug]);

  const handleFilterButtonClick = useCallback(
    (filterType: string) => {
      // Track button click event
      const buttonNameMap: Record<string, string> = {
        nearby: 'near_me',
        solomeal: 'solomeal',
        promotion: 'promotion',
        freeship: 'freeship',
      };

      if (buttonNameMap[filterType]) {
        trackEvent('click_button', {
          action_time: Math.floor(Date.now() / 1000),
          button_name: buttonNameMap[filterType],
          button_location: 'all_store_section',
        });
      }

      const newFilters = { ...tempFilters };

      switch (filterType) {
        case 'nearby':
          newFilters.nearby = !tempFilters.nearby;
          break;
        case 'promotion':
          newFilters.promotion = !tempFilters.promotion;
          break;
        case 'freeship':
          newFilters.freeship =
            tempFilters.freeship === 'freeship' ? false : 'freeship';
          break;
        case 'open_now':
          newFilters.open_now = !tempFilters.open_now;
          break;
        case 'solomeal':
          newFilters.item_tag =
            tempFilters.item_tag === 'SOLO_MEAL' ? '' : 'SOLO_MEAL';
          break;
        default:
          break;
      }

      navigateWithFilters(newFilters);
    },
    [tempFilters, navigateWithFilters]
  );

  const getActiveFilterCount = useCallback(() => {
    let count = 0;
    if (tempFilters.category_slug) count++;
    if (tempFilters.promotion) count++;
    if (tempFilters.freeship) count++;
    if (tempFilters.special_request) count++;
    if (tempFilters.nearby) count++;
    if (tempFilters.open_now) count++;
    return count;
  }, [tempFilters]);

  if (isLoading) {
    return (
      <div className="bg-transparent w-full">
        <div className="flex gap-2 scrollbar-hide overflow-x-scroll items-center px-4 py-3">
          <Skeleton className="h-10 w-24 rounded-xl bg-neutral-10" />
          <Skeleton className="h-10 w-20 rounded-xl bg-neutral-10" />
          <Skeleton className="h-10 w-28 rounded-xl bg-neutral-10" />
          <Skeleton className="h-10 w-20 rounded-xl bg-neutral-10" />
          <Skeleton className="h-10 w-24 rounded-xl bg-neutral-10" />
        </div>
      </div>
    );
  }

  return (
    <div data-testid="home-store-filter" className="bg-transparent w-full">
      <div className="flex gap-2 scrollbar-hide overflow-x-scroll items-center px-4 py-3">
        <FilterDrawer
          isOpen={isFilterOpen}
          setIsOpen={setIsFilterOpen}
          tempFilters={tempFilters}
          setTempFilters={setTempFilters}
          categories={categoriesData?.categories ?? []}
          textValue=""
          activeCount={getActiveFilterCount()}
          onApply={handleApplyFilters}
          onReset={handleResetFilters}
        />

        <Button
          variant="outline"
          data-testid="home-store-filter-nearby"
          onClick={() => handleFilterButtonClick('nearby')}
          className="aha-search-pill rounded-xl"
        >
          Gần tôi
        </Button>

        <Button
          variant="outline"
          data-testid="home-store-filter-solomeal"
          onClick={() => handleFilterButtonClick('solomeal')}
          className="aha-search-pill rounded-xl"
        >
          SOLOMEAL
        </Button>

        <Button
          variant="outline"
          data-testid="home-store-filter-promo"
          onClick={() => handleFilterButtonClick('promotion')}
          className="aha-search-pill rounded-xl"
        >
          Ưu đãi
        </Button>

        <Button
          variant="outline"
          data-testid="home-store-filter-freeship"
          onClick={() => handleFilterButtonClick('freeship')}
          className="aha-search-pill rounded-xl"
        >
          Freeship
        </Button>

        {getActiveFilterCount() > 0 && (
          <Button
            variant="ghost"
            onClick={handleClearAllFilters}
            className="aha-search-pill rounded-xl"
          >
            Xóa bộ lọc
          </Button>
        )}
      </div>
    </div>
  );
}
